import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
  memo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { getNodeInitialValues, addFocusOnFirstField } from '../../../../helpers/formTree';
import ContributeFormNode from '../ContributeFormNode';
import GridContext from '../../../organisms/Grid/GridContext';
import DuplicateBlockWrapper from '../../../organisms/DuplicateBlockWrapper/DuplicateBlockWrapper';
import Grid from '../../../organisms/Grid/Grid';
import BlockControls from '../../../organisms/BlockControls/BlockControls';
import DuplicableBlockFooter from '../../../organisms/DuplicableBlockFooter/DuplicableBlockFooter';
import { FieldArray, change } from 'redux-form';
import {
  ERROR_BELOW_MINROWS,
  ERROR_ABOVE_MAXROWS,
} from '../../../../constants/errorType';
import { useDispatch, useSelector } from 'react-redux';
import { answerRemoveBlock, setAutoFocusRow } from '../../../../store/actions/task';
import { autoFocusRowSelector } from '../../../../store/selectors/task';
import { BLOCK_NODE } from '../../../../constants/formNode';
import arePropsEqual from '../../../../helpers/arePropsEqual';
import { useOcrMapping } from '../../../../contexts/OcrMappingContext';
import { getBlockDarkerColor } from '../../../../helpers/color';
import ComputedHeightsContext from '../../ContributeFormTemplate/ComputedHeightsContext';
import HiddenBoxesContext from '../../ContributeFormTemplate/HiddenBoxesContext';
import RoundedSpinner from '../../../atoms/RoundedSpinner/RoundedSpinner';

const ContributeFormNodeBlockRow = memo(({
  node,
  field,
  readonly,
  isVisible = true,
  brightenBackground = false,
  ...props
}) => {
  const blocks = useContext(ComputedHeightsContext);

  // Get Y positions from computed heights - already reflowed
  const childrenYPositions = useMemo(() => {
    // Extract parent block path from field (e.g., "block.$items[0]" -> "block")
    const blockPathMatch = field.match(/^(.+)\.\$items\[\d+\]$/);
    if (!blockPathMatch) return {};

    const blockPath = blockPathMatch[1];
    const itemIndexMatch = field.match(/\$items\[(\d+)\]$/);
    const itemIndex = itemIndexMatch ? parseInt(itemIndexMatch[1], 10) : 0;

    const blockData = blocks.get(blockPath);
    if (!blockData?.childrenBoxes?.[itemIndex]) return {};

    const positions = {};
    const reflowedBoxes = blockData.childrenBoxes[itemIndex];
    for (let i = 0; i < node.children.length; i++) {
      positions[i] = reflowedBoxes[i]?.y ?? 0;
    }
    return positions;
  }, [field, blocks, node.children.length]);

  if (!isVisible) {
    return <RoundedSpinner />;
  }

  return (
    <>
      {node.children.map((child, i) => {
        const finalY = childrenYPositions[i] ?? (child.nodeType === BLOCK_NODE ? 0 : child.box.y);
        return (
          <ContributeFormNode
            {...props}
            key={i}
            node={child}
            boxOverride={{
              y: finalY,
            }}
            parentFieldName={`${field}.`}
            readonly={readonly}
            isVisible={isVisible}
            brightenBackground={brightenBackground}
          />
        );
      })}
    </>
  );
}, arePropsEqual);

ContributeFormNodeBlockRow.displayName = 'ContributeFormNodeBlockRow';

ContributeFormNodeBlockRow.propTypes = {
  node: PropTypes.object.isRequired,
  field: PropTypes.string.isRequired,
  readonly: PropTypes.bool,
  isVisible: PropTypes.bool,
  brightenBackground: PropTypes.bool,
};

// Virtualization wrapper for one row of a duplicable block.
const VirtualizableRow = memo(({
  nodeHeight,
  defaultVisible,
  parentIsVisible,
  duplicateWrapperProps,
  rowProps,
}) => {
  const [observerVisible, setObserverVisible] = useState(defaultVisible);
  const observerRef = useRef(null);

  // Callback ref to attach IntersectionObserver when element is mounted
  const refCallback = useCallback((node) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (!node) return;

    const appMain = document.getElementById('app-main');
    if (!appMain) {
      setObserverVisible(true);
      return;
    }

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        setObserverVisible(entry.isIntersecting);
      });
    }, {
      root: appMain,
      rootMargin: '1500px',
      threshold: 0,
    });

    observerRef.current.observe(node);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const isVisible = parentIsVisible && observerVisible;

  return (
    <DuplicateBlockWrapper
      ref={refCallback}
      {...duplicateWrapperProps}
      nodeHeight={nodeHeight}
    >
      <ContributeFormNodeBlockRow
        {...rowProps}
        isVisible={isVisible}
      />
    </DuplicateBlockWrapper>
  );
}, arePropsEqual);

VirtualizableRow.displayName = 'VirtualizableRow';

VirtualizableRow.propTypes = {
  nodeHeight: PropTypes.number.isRequired,
  defaultVisible: PropTypes.bool,
  parentIsVisible: PropTypes.bool,
  duplicateWrapperProps: PropTypes.object,
  rowProps: PropTypes.object.isRequired,
};

const ContributeFormNodeBlockFields = memo(({
  node,
  boxOverride,
  fields,
  readonly,
  blocksValues,
  parentFieldName,
  isNestedInDuplicable = false,
  isVisible: parentIsVisible = true,
  brightenBackground = false,
  meta: { error },
  ...props
}) => {
  const grid = useContext(GridContext);
  const blocks = useContext(ComputedHeightsContext);
  const hiddenBoxes = useContext(HiddenBoxesContext);

  // Get block path without $items suffix for lookup in blocks
  const blockPathForLookup = parentFieldName ? `${parentFieldName}${node.id}` : node.id;
  const blockData = blocks.get(blockPathForLookup);
  const folded = blockData?.folded ?? false;

  // Path of the row whose first field must take focus (a row the user just added).
  const autoFocusRow = useSelector((state) => {
    const row = autoFocusRowSelector(state);
    return row && row.startsWith(fields.name) ? row : undefined;
  });

  // re-draw rows when user insert a new one inside the existing list so focus can be done.
  const [insertGeneration, setInsertGeneration] = useState(0);

  // Calculate the brightened color for the background if needed
  const brighterColor = useMemo(() => getBlockDarkerColor(node.color, -0.38), [node.color]);

  // Compute the real height of the child grid using precomputed heights
  const realHeight = useMemo(() => {
    if (blockData?.childrenHeights) {
      // Sum all row heights from precomputed values
      const totalRowsHeight = Object.values(blockData.childrenHeights).reduce(
        (sum, h) => sum + h,
        0,
      );
      const baseHeight = totalRowsHeight + 1; // +1 for header
      return baseHeight + (node.duplicable ? 1 : 0);
    }
    // Fallback if not found
    const baseHeight = fields.length * node.rowHeight + 1;
    return baseHeight + (node.duplicable ? 1 : 0);
  }, [blockData, node.duplicable, node.rowHeight, fields.length]);

  // check if there is duplicated block with hydrated values
  const getHydratedBlock = () => {
    const reviewTree = props?.reviewTree;
    const blockArray = reviewTree?.blockArray;

    if (blockArray && blockArray.length > 0) {
      for (let i = 1; i < blockArray.length; i++) {
        const currentBlock = blockArray[i];
        const previousBlock = blockArray[i - 1];

        if (
          reviewTree[currentBlock.id]?.$items?.length > 1 &&
          previousBlock.box.left === currentBlock.box.left &&
          blocksValues?.[node.id]?.nbFields > 0
        ) {
          const indexToRemove = fields.length;
          const init = () => fields.push(getNodeInitialValues(node));
          const end = () => fields.remove(indexToRemove);

          setTimeout(init, 10);
          setTimeout(end, 20);
        }
      }
    }
  };

  const realBox = useMemo(
    () => {
      let finalHeight;
      if (folded) {
        finalHeight = 1;
      } else if (fields.length === 0) {
        if (node.duplicable) {
          finalHeight = 2;
        } else {
          finalHeight = 1;
        }
      } else {
        finalHeight = Math.min(
          realHeight,
          node.maxRowsEnabled ? node.maxRows : Number.MAX_SAFE_INTEGER,
        );
      }
      return {
        ...node.box,
        y: boxOverride ? boxOverride.y : node.box.y,
        height: finalHeight,
      };
    },
    [
      node.box,
      node.duplicable,
      node.maxRows,
      node.maxRowsEnabled,
      boxOverride?.y,
      realHeight,
      folded,
      fields.length,
    ],
  );

  const style = useMemo(
    () => ({
      background: brightenBackground ? brighterColor : node.color,
      // Enable overflow auto if real height is greater than allowed height
      overflowY: !folded && realHeight > realBox.height ? 'auto' : 'hidden',
      overflowX: 'hidden',
    }),
    [
      node.iterativeBackground,
      node.color,
      folded,
      realHeight,
      realBox.height,
      brightenBackground,
      brighterColor,
    ],
  );

  useEffect(() => {
    if (fields.length === 0 && !node.duplicable) {
      fields.push({});
    }
    getHydratedBlock();
  }, []);

  // Handle delete button in block item controls
  // This will remove the field from the answer and the row from the redux-form array
  const dispatch = useDispatch();
  const { shiftIndicesAfterDelete } = useOcrMapping();

  // Memoized functions to prevent re-renders
  const handleDeleteDuplicate = useCallback(
    (name, i) => {
      if (!props.isReview) {
        // If we are not in a review, we delete answers attached to the block
        // so hydrated blocks and fields with "color" are cleaned
        dispatch(answerRemoveBlock(name));
      }
      // Shift indices in the OCR mapping
      // Extract the block path (everything up to the last $items[X])
      const blockPath = name.match(/(.*)\.\$items\[\d+\]$/)?.[1] || name;
      shiftIndicesAfterDelete(blockPath, i);

      // Remove the field from the form (redux-form renumbers automatically)
      fields.remove(i);
    },
    [fields, shiftIndicesAfterDelete],
  );

  const handleAddDuplicate = useCallback(() => {
    dispatch(setAutoFocusRow(`${fields.name}[${fields.length}]`));
    fields.push(getNodeInitialValues(node));
  }, [dispatch, fields, node]);

  const handleInsertDuplicate = useCallback(
    (i) => {
      dispatch(setAutoFocusRow(`${fields.name}[${i + 1}]`));
      // Force a one-time remount of the rows so the inserted row mounts and focuses
      setInsertGeneration((g) => g + 1);
      fields.insert(i + 1, getNodeInitialValues(node));
    },
    [dispatch, fields, node],
  );

  const handleDeleteAllDuplicatesClick = useCallback(() => {
    try {
      if (!props.isReview) {
        // If we are not in a review, we delete answers attached to all children blocks
        // so hydrated blocks and fields with "color" are cleaned
        // Build the block path
        const blockPath = parentFieldName ? `${parentFieldName}${node.id}` : node.id;
        // Remove the entire block from the answer
        dispatch(answerRemoveBlock(blockPath));
      }
      // Safely empty all block rows in redux-form
      const fieldsLength = fields.length;
      for (let i = fieldsLength - 1; i >= 0; i--) {
        fields.remove(i);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error removing block:', error);
    }
  }, [dispatch, fields, parentFieldName, node.id]);

  const handleFoldClick = useCallback(() => {
    dispatch(change('contribute', `${blockPathForLookup}.folded`, !folded));
  }, [dispatch, blockPathForLookup, folded]);

  // If the node is duplicable with iterativeBackground to truz, don't propagate the
  // parent's brightenBackground to children - each row will calculate its own
  // brightenBackground based on its index
  const shouldBrightenChildrenBackground = node.iterativeBackground ? false : brightenBackground;

  const reviewOptions = blocksValues ? blocksValues[node.id] : null;
  // We need to hide the header if it has no content and if the block is a "blocks" container.
  const blockHasNoHeader = !node.foldable
    && !node.nameEnabled
    && !node.duplicable
    && (!reviewOptions || reviewOptions.nbFields === 0)
    && (!node.children || node.children.every(child => child.nodeType === BLOCK_NODE));
  return (
    <Grid.Box
      {...realBox}
      style={style}
      isNestedInDuplicable={isNestedInDuplicable}
    >
      {!blockHasNoHeader && (
        <BlockControls
          {...props}
          block={node}
          folded={folded}
          duplicable={node.rowHeight !== 0}
          onDeleteAllClick={handleDeleteAllDuplicatesClick}
          onFoldClick={handleFoldClick}
          readonly={readonly}
          reviewOptions={reviewOptions}
          fieldName={fields.name}
          totalDuplicates={fields.length}
          error={error}
        />
      )}
      {!folded && (
        <Grid
          columns={node.box.width}
          rowHeight={grid.rowHeight}
          fixedRows={1}
          gutter={0}
          dotBackground={false}
        >
          {fields.map((field, i) => {
            const rowKey = `${insertGeneration}-${i}`;
            // Get row height from precomputed heights
            const nodeHeight = blockData?.childrenHeights?.[i] ?? node.rowHeight;

            // Calculate whether the children backgrounds should be brightened : inherit from
            // parent thanks to shouldBrightenChildrenBackground OR calculate based
            // on local index (even) if iterativeBackground is enabled
            let childrenBrightenBackground = shouldBrightenChildrenBackground
              || (node.iterativeBackground && (i + 1) % 2 === 0);

            // Focus the first visible field:
            // - row 0 when the block is flagged focusFirstRow (initial page focus)
            // - the row was just added by the user (matched by its path in the store)
            const needsFocus = !readonly && (
              (i === 0 && node.focusFirstRow) || field === autoFocusRow
            );
            let nodeForRow = node;
            if (needsFocus) {
              nodeForRow = {
                ...node,
                focusFirstRow: false,
                children: addFocusOnFirstField(node.children, hiddenBoxes, field),
              };
            }

            const rowProps = {
              ...props,
              isNestedInDuplicable: node.duplicable,
              node: nodeForRow,
              field: field,
              nodeHeight: nodeHeight,
              readonly: readonly,
              blocksValues: blocksValues,
              brightenBackground: childrenBrightenBackground,
            };

            // Non-duplicable blocks: single row, always visible, no virtualization needed
            if (!node.duplicable) {
              return (
                <ContributeFormNodeBlockRow
                  key={rowKey}
                  {...rowProps}
                  isVisible={parentIsVisible}
                />
              );
            }

            // Duplicable blocks: virtualize each row via IntersectionObserver
            // By default, rows at index < 5 or newly inserted rows
            // start visible then the observer takes over.
            const defaultVisible = !(fields.length > 5 && i >= 5) || field === autoFocusRow;
            return (
              <VirtualizableRow
                key={rowKey}
                nodeHeight={nodeHeight}
                defaultVisible={defaultVisible}
                parentIsVisible={parentIsVisible}
                duplicateWrapperProps={{
                  increment: i + 1,
                  total: fields.length,
                  color: node.color,
                  brightenBackground: childrenBrightenBackground,
                  brighterColor: brighterColor,
                  readonly: readonly,
                  onAddClick: () => handleInsertDuplicate(i),
                  onDelete: () => handleDeleteDuplicate(field, i),
                  showIncrement: node.showIncrement,
                }}
                rowProps={rowProps}
              />
            );
          })
          }
          {node.duplicable && (
            <DuplicableBlockFooter
              color={node.color}
              onAddClick={handleAddDuplicate}
              readonly={readonly}
            />
          )}
        </Grid>
      )}
    </Grid.Box>
  );
}, arePropsEqual);

ContributeFormNodeBlockFields.displayName = 'ContributeFormNodeBlockFields';

ContributeFormNodeBlockFields.propTypes = {
  node: PropTypes.object,
  reviewTree: PropTypes.object,
  boxOverride: PropTypes.object,
  fields: PropTypes.object,
  readonly: PropTypes.bool,
  blocksValues: PropTypes.object,
  parentFieldName: PropTypes.string,
  isNestedInDuplicable: PropTypes.bool,
  isReview: PropTypes.bool,
  isVisible: PropTypes.bool,
  brightenBackground: PropTypes.bool,
  meta: PropTypes.shape({
    error: PropTypes.object,
  }),
};

const ContributeFormNodeBlock = memo(({ node, parentFieldName = '', ...props }) => {
  const validate = useCallback(
    (rows) => {
      if (node.rowsRange) {
        if (
          typeof node.rowsRange?.min === 'number' &&
          rows &&
          rows.length < node.rowsRange.min
        ) {
          return {
            type: ERROR_BELOW_MINROWS,
            value: node.rowsRange.min,
          };
        } else if (
          typeof node.rowsRange?.max === 'number' &&
          rows.length > node.rowsRange.max
        ) {
          return {
            type: ERROR_ABOVE_MAXROWS,
            value: node.rowsRange.max,
          };
        }
      }
    },
    [node.rowsRange?.min, node.rowsRange?.max],
  );
  // Block rows are implemented as redux-form FieldArray to be in syc with the form state

  return (
    <FieldArray
      name={`${parentFieldName}${node.id}.$items`}
      node={node}
      parentFieldName={parentFieldName}
      component={ContributeFormNodeBlockFields}
      validate={validate}
      {...props}
    />
  );
}, arePropsEqual);

ContributeFormNodeBlock.displayName = 'ContributeFormNodeBlock';

ContributeFormNodeBlock.propTypes = {
  node: PropTypes.object,
  parentFieldName: PropTypes.string,
  isVisible: PropTypes.bool,
};

export default ContributeFormNodeBlock;
